import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Row, Col, Alert, Card } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useStore } from '../../../stores/store';
import { PageHeader, StyledCard, EmptyState } from '../../Common/UI';
import './Profile.css';

const Profile = () => {
    const { t } = useTranslation();
    const axiosPrivate = useAxiosPrivate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [profileData, setProfileData] = useState(null);
    const { mainStore } = useStore();
    useSetMenu(mainStore.ProfileMenuType, mainStore.ProfileMenuNo);
    const { reset } = useForm();

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetchProfile = async () => {
            try {
                const response = await axiosPrivate.get('/StaffPortalStaffs/GetProfile', {
                    signal: controller.signal,
                });
                if (isMounted) {
                    const data = { ...response.data };
                    if (data.birthDate) {
                        const date = new Date(data.birthDate);
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        data.birthDate = `${day}.${month}.${year}`;
                    }
                    setProfileData(data);
                    reset(data);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err.response?.data?.message || t('Error fetching staff profile data'));
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchProfile();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [axiosPrivate, reset, t]);

    const getInitials = () => {
        if (!profileData) return '';
        const firstName = profileData.firstName || '';
        const lastName = profileData.lastName || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    const getPhotoUrl = () => {
        if (profileData?.photoData) {
            return `data:image/jpeg;base64,${profileData.photoData}`;
        }
        return null;
    };

    if (loading) {
        return (
            <div className="profile-container">
                <div className="text-center py-5">
                    <CircularProgress sx={{ color: 'var(--brand-red, #dc354d)' }} />
                    <p className="mt-3 text-muted">{t('Loading profile data...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-container">
                <Alert variant="danger" className="mb-4">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                </Alert>
            </div>
        );
    }

    if (!profileData) {
        return (
            <div className="profile-container">
                <PageHeader
                    icon="person-badge-fill"
                    title={t('staffProfile')}
                    subtitle={t('viewYourProfileInformation')}
                />
                <EmptyState
                    icon="person-x"
                    title={t('No Profile Data')}
                    message={t('No staff profile data available')}
                />
            </div>
        );
    }

    return (
        <div className="profile-container">
            <PageHeader
                icon="person-badge-fill"
                title={t('staffProfile')}
                subtitle={t('viewYourProfileInformation')}
            />

            {/* Profile Header Card */}
            <StyledCard className="profile-header-card mb-4" hoverable={false}>
                <Card.Body>
                    <div className="profile-header-content">
                        <div className="profile-avatar-section">
                            <div className="profile-avatar">
                                {getPhotoUrl() ? (
                                    <img
                                        src={getPhotoUrl()}
                                        alt={`${profileData.firstName} ${profileData.lastName}`}
                                        className="profile-avatar-img"
                                    />
                                ) : (
                                    <div className="profile-avatar-initials">
                                        {getInitials()}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="profile-header-info">
                            <h3 className="profile-name">
                                {profileData.firstName} {profileData.lastName}
                            </h3>
                            <div className="profile-meta">
                                <div className="meta-item">
                                    <i className="bi bi-person-badge me-2"></i>
                                    <span className="meta-label">{t('Code')}:</span>
                                    <span className="meta-value">{profileData.code}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-envelope me-2"></i>
                                    <span className="meta-label">{t('Official Email')}:</span>
                                    <span className="meta-value">{profileData.officialEmail}</span>
                                </div>
                                {profileData.employmentType && (
                                    <div className="meta-item">
                                        <i className="bi bi-briefcase me-2"></i>
                                        <span className="meta-label">{t('Employment')}:</span>
                                        <span className="meta-value">{profileData.employmentType}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </StyledCard>

            {/* Information Sections */}
            <Row xs={1} md={2} className="g-4">
                {/* Personal Information */}
                <Col>
                    <StyledCard className="h-100">
                        <Card.Body>
                            <h5 className="card-title mb-3">
                                <i className="bi bi-person-fill me-2"></i>
                                {t('personalInformation')}
                            </h5>
                            <div className="profile-meta-section">
                                <div className="meta-item">
                                    <i className="bi bi-credit-card-2-front me-2"></i>
                                    <span className="meta-label">{t('profilePersonalNumber')}:</span>
                                    <span className="meta-value">{profileData.personalNumber || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-cake2 me-2"></i>
                                    <span className="meta-label">{t('profileBirthDate')}:</span>
                                    <span className="meta-value">{profileData.birthDate || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-gender-ambiguous me-2"></i>
                                    <span className="meta-label">{t('profileGender')}:</span>
                                    <span className="meta-value">{profileData.gender || '-'}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </StyledCard>
                </Col>

                {/* Contact Information */}
                <Col>
                    <StyledCard className="h-100">
                        <Card.Body>
                            <h5 className="card-title mb-3">
                                <i className="bi bi-envelope-fill me-2"></i>
                                {t('contactInformation')}
                            </h5>
                            <div className="profile-meta-section">
                                <div className="meta-item">
                                    <i className="bi bi-envelope-at me-2"></i>
                                    <span className="meta-label">{t('profileOfficialEmail')}:</span>
                                    <span className="meta-value">{profileData.officialEmail || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-envelope me-2"></i>
                                    <span className="meta-label">{t('profilePrivateEmail')}:</span>
                                    <span className="meta-value">{profileData.privateEmail || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-telephone me-2"></i>
                                    <span className="meta-label">{t('profilePhone')}:</span>
                                    <span className="meta-value">{profileData.phone || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-phone me-2"></i>
                                    <span className="meta-label">{t('profileMobile')}:</span>
                                    <span className="meta-value">{profileData.mobile || '-'}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </StyledCard>
                </Col>

                {/* Professional Information */}
                <Col>
                    <StyledCard className="h-100">
                        <Card.Body>
                            <h5 className="card-title mb-3">
                                <i className="bi bi-briefcase-fill me-2"></i>
                                {t('professionalInformation')}
                            </h5>
                            <div className="profile-meta-section">
                                <div className="meta-item">
                                    <i className="bi bi-diagram-3 me-2"></i>
                                    <span className="meta-label">{t('profileEmploymentType')}:</span>
                                    <span className="meta-value">{profileData.employmentType || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-mortarboard me-2"></i>
                                    <span className="meta-label">{t('profileTeachingLevel')}:</span>
                                    <span className="meta-value">{profileData.teachingLevel || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-book me-2"></i>
                                    <span className="meta-label">{t('profileRelevantField')}:</span>
                                    <span className="meta-value">{profileData.relevantField || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-translate me-2"></i>
                                    <span className="meta-label">{t('profileRelevantFieldEng')}:</span>
                                    <span className="meta-value">{profileData.relevantFieldEng || '-'}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </StyledCard>
                </Col>

                {/* Address Information */}
                <Col>
                    <StyledCard className="h-100">
                        <Card.Body>
                            <h5 className="card-title mb-3">
                                <i className="bi bi-house-fill me-2"></i>
                                {t('addressInformation')}
                            </h5>
                            <div className="profile-meta-section">
                                <div className="meta-item">
                                    <i className="bi bi-geo me-2"></i>
                                    <span className="meta-label">{t('profileMunicipality')}:</span>
                                    <span className="meta-value">{profileData.municipality || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-geo-alt-fill me-2"></i>
                                    <span className="meta-label">{t('profileResidence')}:</span>
                                    <span className="meta-value">{profileData.residence || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-house-door me-2"></i>
                                    <span className="meta-label">{t('profileAddress')}:</span>
                                    <span className="meta-value">{profileData.address || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <i className="bi bi-mailbox me-2"></i>
                                    <span className="meta-label">{t('profileZipCode')}:</span>
                                    <span className="meta-value">{profileData.zipCode || '-'}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </StyledCard>
                </Col>

                {/* Office & Banking Information */}
                {(profileData.building || profileData.office || profileData.bankAccount) && (
                    <Col md={12}>
                        <StyledCard>
                            <Card.Body>
                                <h5 className="card-title mb-3">
                                    <i className="bi bi-building me-2"></i>
                                    {t('officeAndBankingInformation')}
                                </h5>
                                <Row>
                                    <Col md={6}>
                                        <div className="profile-meta-section">
                                            <div className="meta-item">
                                                <i className="bi bi-building me-2"></i>
                                                <span className="meta-label">{t('profileBuilding')}:</span>
                                                <span className="meta-value">{profileData.building || '-'}</span>
                                            </div>
                                            <div className="meta-item">
                                                <i className="bi bi-door-open me-2"></i>
                                                <span className="meta-label">{t('profileOffice')}:</span>
                                                <span className="meta-value">{profileData.office || '-'}</span>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="profile-meta-section">
                                            <div className="meta-item">
                                                <i className="bi bi-credit-card me-2"></i>
                                                <span className="meta-label">{t('profileBankAccount')}:</span>
                                                <span className="meta-value">{profileData.bankAccount || '-'}</span>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </StyledCard>
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default Profile;